import { Button, Option } from '@fluentui/react-components';
import { ChevronLeft20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from '../Dropdown/Dropdown';
import { Text } from '../Text/Text';
import { PaginatorProps } from './Paginator.interfaces';
import './Paginator.scss';
import { renderPageButtons } from './Paginator.utils';

export const Paginator: FC<PaginatorProps> = (props) => {
  const {
    currentPage,
    maxPage,
    maxEntriesPerPage,
    entriesPerPageSteps,
    entriesPerPageDefaultValue,
    pageChanged,
    entriesPerPageChanged,
  } = props;
  const { t } = useTranslation();
  const firstPage = currentPage ?? 1;
  const [localCurrentPage, setLocalCurrentPage] = useState(firstPage);
  const defaultButtonRange = 2;

  useEffect(() => {
    pageChanged?.(localCurrentPage);
    // Only notify on an actual page change. `pageChanged` is intentionally left
    // out of the deps: callers pass a non-memoized inline callback, so including
    // it would re-run on every parent render (and could loop via the currentPage
    // round-trip). The effect already captures the latest callback whenever
    // localCurrentPage changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localCurrentPage]);

  useEffect(() => {
    if (currentPage) {
      setLocalCurrentPage(currentPage);
    }
  }, [currentPage]);

  const navigateLeftButton = (
    <Button
      key="leftButton"
      className="pageButton"
      appearance="subtle"
      disabled={localCurrentPage === 1}
      icon={<ChevronLeft20Regular />}
      aria-label={t('translation:paginator:previous_page', {
        defaultValue: 'Vorherige Seite',
      })}
      onClick={() => setLocalCurrentPage(localCurrentPage - 1)}
    />
  );

  const navigateRightButton = (
    <Button
      key="rightButton"
      className="pageButton"
      appearance="subtle"
      disabled={localCurrentPage === maxPage}
      icon={<ChevronRight20Regular />}
      aria-label={t('translation:paginator:next_page', {
        defaultValue: 'Nächste Seite',
      })}
      onClick={() => setLocalCurrentPage(localCurrentPage + 1)}
    />
  );

  const firstPageButton =
    localCurrentPage !== 1 ? (
      <Button
        key="firstPageButton"
        className="pageButton"
        appearance="subtle"
        onClick={() => setLocalCurrentPage(1)}
      >
        {1}
      </Button>
    ) : null;

  const lastPageButton =
    localCurrentPage !== maxPage ? (
      <Button
        key="lastPageButton"
        className="pageButton"
        appearance="subtle"
        onClick={() => setLocalCurrentPage(maxPage)}
      >
        {maxPage}
      </Button>
    ) : null;

  const currentPageButton = (
    <Button key="currentPageButton" className="pageButton" appearance="primary" shape="circular">
      {localCurrentPage}
    </Button>
  );

  const getFirstPreviousButton = (): number => {
    //Quantity of 'next pages buttons'
    const buttonsUsedForNextButtons = maxPage - (localCurrentPage + 1);
    //Quantity of buttons not renderered for 'next buttons'
    const remainingButtons = defaultButtonRange - buttonsUsedForNextButtons;
    const range = remainingButtons > 0 ? remainingButtons + defaultButtonRange : defaultButtonRange;
    let firstButtonNumber = localCurrentPage - range;
    while (firstButtonNumber <= 1) {
      ++firstButtonNumber;
    }
    return firstButtonNumber;
  };

  const getLastNextButton = (): number => {
    //Quantity of 'previous pages buttons'
    const buttonsUsedForPreviousButtons = localCurrentPage - 2;
    //Quantity of buttons not rendered for 'previous buttons'
    const remainingButtons = defaultButtonRange - buttonsUsedForPreviousButtons;
    const range = remainingButtons > 0 ? remainingButtons + defaultButtonRange : defaultButtonRange;

    let lastButtonNumber = localCurrentPage + range;
    while (lastButtonNumber >= maxPage) {
      --lastButtonNumber;
    }
    return lastButtonNumber;
  };

  const previousPages =
    localCurrentPage - 1 > 1
      ? renderPageButtons(getFirstPreviousButton(), localCurrentPage - 1, (pageNumber) =>
          setLocalCurrentPage(pageNumber),
        )
      : null;

  const nextPages =
    localCurrentPage + 1 < maxPage
      ? renderPageButtons(localCurrentPage + 1, getLastNextButton(), (pageNumber) =>
          setLocalCurrentPage(pageNumber),
        )
      : null;

  const getEntriesPerPageSelection = () => {
    const entrySelection: number[] = [];
    for (let i = entriesPerPageSteps; i <= maxEntriesPerPage; i = i + entriesPerPageSteps) {
      entrySelection.push(i);
    }
    if (!entrySelection.includes(maxEntriesPerPage)) {
      entrySelection.push(maxEntriesPerPage);
    }
    return entrySelection;
  };

  return maxPage > 0 ? (
    <div className="paginator flex items-center w-full">
      <div className="flex gap-2">
        {navigateLeftButton}
        {firstPageButton}
        {previousPages?.map((button) => button)}
        {currentPageButton}
        {nextPages?.map((button) => button)}
        {lastPageButton}
        {navigateRightButton}
      </div>
      <div className="flex gap-2 items-center justify-end w-full">
        <Text>{t('entries_per_page', { defaultValue: 'Einträge pro Seite' })}</Text>
        <Dropdown
          defaultValue={String(entriesPerPageDefaultValue ?? 50)}
          defaultSelectedOptions={[String(entriesPerPageDefaultValue ?? 50)]}
          onOptionSelect={(_, data) => {
            if (data.optionValue) {
              entriesPerPageChanged?.(Number(data.optionValue));
            }
          }}
          className="entriesPerPageDropdown"
        >
          {getEntriesPerPageSelection().map((entry) => (
            <Option key={entry} value={String(entry)}>
              {String(entry)}
            </Option>
          ))}
        </Dropdown>
      </div>
    </div>
  ) : null;
};
